import { useRef, useState } from 'react';
import type { ProductDetailDto } from '@ecom/contracts';
import { useCatalogStore } from '@/stores/catalog.store';
import { useT } from '@/i18n/use-t';
import { BilingualField, type BilingualValue } from '@/components/fields';
import { Alert, Button, Field, Input } from '@/components/ui';
import { PlusIcon, TrashIcon } from '@/components/icons';
import { Section } from '@/components/page';
import { mediaUrl } from '@/lib/media';

/**
 * Films (R4) — how a cloth falls, how a bag opens.
 *
 * They play after the photographs on the product page and load nothing but
 * their poster until opened, so a poster is worth adding: without one the
 * thumbnail is a plain tile.
 */
export function VideosCard({ product }: { product: ProductDetailDto }) {
  const t = useT();
  const { saving, error, addVideo, removeVideo } = useCatalogStore();
  const videoInput = useRef<HTMLInputElement>(null);
  const posterInput = useRef<HTMLInputElement>(null);
  const [label, setLabel] = useState<BilingualValue>({ ar: '', en: '' });
  const [seconds, setSeconds] = useState('');
  const [adding, setAdding] = useState(false);

  async function upload() {
    const video = videoInput.current?.files?.[0];
    if (!video) return;
    const ok = await addVideo(
      product.id,
      { video, poster: posterInput.current?.files?.[0] ?? null },
      {
        durationSeconds: Number.parseInt(seconds, 10) || null,
        label: label.en.trim() ? { en: label.en.trim(), ...(label.ar.trim() ? { ar: label.ar.trim() } : {}) } : null,
      },
    );
    if (ok) {
      setAdding(false);
      setLabel({ ar: '', en: '' });
      setSeconds('');
    }
  }

  return (
    <Section
      title={t('films')}
      subtitle={t('filmsHint')}
      flush
      action={
        !adding ? (
          <Button variant="secondary" size="sm" onClick={() => setAdding(true)}>
            <PlusIcon className="size-4" />
            {t('addFilm')}
          </Button>
        ) : null
      }
    >

      {product.videos.length === 0 && !adding ? (
        <p className="px-5 pb-5 text-sm text-ink-muted">{t('noFilms')}</p>
      ) : null}

      {product.videos.length > 0 ? (
        <ul className="grid gap-3 px-5 pb-5 sm:grid-cols-2">
          {product.videos.map((video) => (
            <li key={video.id} className="flex flex-col gap-2 rounded-control border border-line p-2">
              <video
                controls
                preload="none"
                poster={mediaUrl(video.posterUrl)}
                className="aspect-video w-full rounded-control bg-surface-sunken object-cover"
              >
                <source src={mediaUrl(video.url) ?? ''} />
              </video>
              <div className="flex items-center justify-between gap-2">
                <span className="truncate text-sm text-ink">{video.label || t('untitledFilm')}</span>
                <Button variant="ghost" size="sm" disabled={saving} onClick={() => void removeVideo(video.id)}>
                  <TrashIcon className="size-4" />
                  {t('remove')}
                </Button>
              </div>
            </li>
          ))}
        </ul>
      ) : null}

      {adding ? (
        <div className="flex flex-col gap-3 border-t border-line p-5">
          <Field label={t('filmFile')} htmlFor="film-file" hint={t('filmFileHint')}>
            <input id="film-file" ref={videoInput} type="file" accept="video/mp4,video/webm" className="text-sm" />
          </Field>
          <Field label={t('filmPoster')} htmlFor="film-poster" hint={t('filmPosterHint')}>
            <input
              id="film-poster"
              ref={posterInput}
              type="file"
              accept="image/jpeg,image/png,image/webp,image/avif"
              className="text-sm"
            />
          </Field>
          <BilingualField label={t('filmLabel')} value={label} onChange={setLabel} />
          <Field label={t('filmSeconds')} htmlFor="film-seconds">
            <Input
              id="film-seconds"
              dir="ltr"
              inputMode="numeric"
              value={seconds}
              onChange={(event) => setSeconds(event.target.value)}
              className="w-28"
            />
          </Field>
          {error ? <Alert>{error}</Alert> : null}
          <div className="flex justify-end gap-2">
            <Button variant="secondary" size="sm" onClick={() => setAdding(false)} disabled={saving}>
              {t('cancel')}
            </Button>
            <Button size="sm" onClick={() => void upload()} disabled={saving}>
              {saving ? t('saving') : t('uploadFilm')}
            </Button>
          </div>
        </div>
      ) : null}
    </Section>
  );
}
